<template>
  <lm-modal
    :ref="modalName"
    :name="modalName"
    :type="modalType"
    body-id="ModalTopicBody"
    class="ModalTopic"
    :bg-header="color"
    :title="title"
    full-height
    remove-footer
    :slider-prev="prevTopic"
    :slider-next="nextTopic"
    @next="onNext"
    @prev="onPrev"
    @[Events.BEFORE_CLOSE]="cleanQuery"
    @[Events.AFTER_CLOSE]="modalClosed"
  >
    <template v-if="currentTopic">
      <div
        class="flex justify-between items-center px-4 py-3 border-b desktop:px-5"
      >
        <lm-breadcrumb-summary
          :breadcrumb="currentTopic.breadcrumb"
          :color="color"
        />
        <related-actors :topic="currentTopic" />
      </div>

      <div class="px-5 py-6 bg-gray-100">
        <div class="flex items-center">
          <user-info :id="currentTopic.author" compacted>
            <span :title="currentTopic.createdAt | formattedDate">
              {{ currentTopic.createdAt | dateFromNow('Now') }}
            </span>
          </user-info>
          <topic-actions
            :topic="currentTopic"
            class="ml-auto"
            detail-view
            @edit-topic="editTopic"
            @open-comments="gotoComments"
            @[Events.TOPIC_DELETED]="closeModal"
          />
        </div>
        <lm-detail-body
          :text="currentTopic.text"
          :edited="currentTopic.editedBy"
          disable-show-more
        />
        <related-section
          v-if="showInsights"
          class="mb-6"
          section="insight"
          :items="insights"
        />
        <related-section
          v-if="showPremises"
          class="mb-6"
          section="premise"
          :items="premises"
        />
        <lm-tag-list :tags="currentTopic.tags" nav-disable class="mb-4" />
        <div class="flex items-center">
          <lm-vote-button
            class="mr-2"
            :votes="currentTopic.positiveVotes"
            :state="currentTopic.currentUser.positiveVote"
            @click="voteTopic"
          />
          <lm-reply-button :author-id="currentTopic.author" />
        </div>
      </div>
      <lm-detail-comments
        id="CommentsBlock"
        ref="comments"
        class="py-6 px-5 desktop:px-6"
        :current-item="currentTopic"
        section="topics"
        @edit="editComment"
        @delete="deleteComment"
        @vote="voteComment"
      />
    </template>
  </lm-modal>
</template>

<script>
import RelatedSection from '@/components/landscape/topics/RelatedSection'
import TopicActions from '@/components/landscape/topics/TopicActions'
import UserInfo from '@/components/landscape/topics/UserInfo'
import LmDetailBody from '@/components/ui/detail/LmDetailBody'
import LmDetailComments from '@/components/ui/detail/LmDetailComments'
import LmBreadcrumbSummary from '@/components/ui/LmBreadcrumbSummary'
import LmReplyButton from '@/components/ui/LmReplyButton'
import LmTagList from '@/components/ui/LmTagList'
import LmVoteButton from '@/components/ui/LmVoteButton'
import {mapGetters} from 'vuex'
import EventHub from '@/lib/eventHub'
import LmModal from '@/components/ui/LmModal'
import {Events, Modal, ModalType} from '@/lib/definitions'
import VueScrollTo from 'vue-scrollto'
import RelatedActors from '@/components/landscape/topics/RelatedActors'

export default {
  name: 'ModalTopic',
  components: {
    LmReplyButton,
    LmVoteButton,
    RelatedSection,
    LmTagList,
    LmDetailBody,
    TopicActions,
    UserInfo,
    LmDetailComments,
    LmBreadcrumbSummary,
    RelatedActors,
    LmModal
  },
  data() {
    return {
      modalName: Modal.TOPIC,
      modalType: ModalType.TOPIC,
      ready: false,
      fromDelete: false,
      Events
    }
  },
  computed: {
    ...mapGetters([
      'currentTopic',
      'currentLandscape',
      'hasManagerRights',
      'inAuthorWindowPermission',
      'getUserByID',
      'prevTopic',
      'nextTopic',
      'isStartup'
    ]),
    color() {
      return this.currentTopic ? this.currentTopic.color : ''
    },
    title() {
      return this.currentTopic
        ? this.currentTopic.title
        : 'Loading currentTopic...'
    },
    insights() {
      return this.currentTopic.insights
    },
    showInsights() {
      return !this.isStartup && this.insights && this.insights.length > 0
    },
    premises() {
      return this.currentTopic.premises
    },
    showPremises() {
      return this.isStartup && this.premises && this.premises.length > 0
    },
    votedPositive() {
      return this.currentTopic.currentUser.positiveVote || false
    }
  },
  watch: {
    currentLandscape() {
      this.loadTopic()
    },
    $route(to, from) {
      const fqt = from.query && from.query.topic
      const tqt = to.query && to.query.topic
      if (fqt && tqt && fqt !== tqt) {
        this.loadTopic(true)
      }
    }
  },
  created() {
    this.loadTopic()
  },
  methods: {
    adminRights(obj) {
      return this.hasManagerRights || this.inAuthorWindowPermission(obj)
    },
    loadTopic(force = false) {
      if (this.ready && !force) return
      this.$store
        .dispatch('loadTopic', this.$route.query.topic)
        .then(() => {
          this.ready = true
          if (this.$route.query.edit) {
            this.$router.removeKeyFromQuery('edit')
            this.$router.pushQuery({modal: Modal.EDIT_TOPIC})
          }
          if (this.$route.query.c) {
            let index = this.$route.query.c
            if (index.length > 4) {
              // is id
              index =
                this.currentTopic.comments.findIndex(
                  comment => comment._id === index
                ) + 1
            }
            this.scrollTo('#comment' + index)
          }
        })
        .catch(e => {
          console.log('Load topic => ', e)
        })
    },
    gotoComments() {
      this.scrollTo('#CommentsBlock')
    },
    editTopic() {
      this.$router.pushQuery({modal: Modal.EDIT_TOPIC})
    },
    voteTopic() {
      let payload = {
        topic: this.currentTopic,
        vote: {positiveVote: !this.votedPositive}
      }
      this.$store.dispatch('voteTopic', payload)
    },
    editComment(commentId) {
      this.$store.dispatch('setComment', commentId)
      this.$router.push({
        query: {
          topic: this.currentTopic._id,
          modal: Modal.EDIT_COMMENT
        }
      })
    },
    voteComment(payload) {
      this.$store.dispatch('voteComment', payload)
    },
    deleteComment(commentId) {
      this.$store.dispatch('deleteComment', commentId)
      EventHub.emit(Events.SHOW_SNACKBAR, 'Comment deleted')
    },
    closeModal() {
      this.fromDelete = true
      this.$refs[this.modalName].close()
    },
    cleanQuery() {
      this.$router.removeKeyFromQuery('c')
    },
    modalClosed() {
      if (this.fromDelete) EventHub.emit(Events.SHOW_SNACKBAR, 'Topic deleted')
      this.$store.dispatch('markCommentsAsRead')
      this.$store.dispatch('unloadTopic')
    },
    scrollTo(anchor) {
      let options = {container: '#ModalTopicBody'}
      VueScrollTo.scrollTo(anchor, 700, options)
    },
    onPrev() {
      if (this.prevTopic) {
        this.$router.pushQuery({
          topic: this.prevTopic,
          search: this.$route.query.search
        })
      }
    },
    onNext() {
      if (this.nextTopic) {
        this.$router.pushQuery({
          topic: this.nextTopic,
          search: this.$route.query.search
        })
      }
    }
  }
}
</script>

<style>
.ModalTopic {
  .Modal-header {
    padding: 20px;
    box-shadow: none;
    .Title {
      font-family: theme('fontFamily.sans');
      font-size: 18px;
      line-height: 1.4;
      text-transform: none;
    }
  }
  .Modal-body {
    padding: 0 !important;
  }
}
</style>
